import { useState } from "react";
import { Users, Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useLoad, Loading, EmptyState, Panel, DataTable, StageBadge, fmtDate, STAGE_LABELS, STAGE_ORDER } from "./shared";
import { ClientDetailSheet } from "./ClientDetailSheet";

const NewClientDialog = ({ call, onClose, onSaved }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "" });
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    if (!form.name || !form.email) return toast.error("Name and email are required");
    await call("/clients", { method: "POST", body: { ...form, source: "manual" } });
    toast.success("Client saved (existing contact reused if email matched)"); onSaved();
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="new-client-dialog">
        <DialogHeader><DialogTitle>Add Commerce client</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {[["name", "Full name *"], ["email", "Email *"], ["phone", "Phone"], ["company", "Company"]].map(([k, l]) => (
            <div key={k} className="space-y-1.5"><Label>{l}</Label><Input value={form[k]} onChange={set(k)} data-testid={`new-client-${k}`} /></div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={save} data-testid="new-client-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const CommerceClients = ({ call }) => {
  const [stage, setStage] = useState("all");
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const params = new URLSearchParams({ ...(stage !== "all" && { stage }), ...(query && { q: query }) }).toString();
  const { data, loading, reload } = useLoad(call, `/clients${params ? `?${params}` : ""}`);
  const [selected, setSelected] = useState(null);
  const [adding, setAdding] = useState(false);
  const rows = data?.clients || [];

  return (
    <div className="space-y-4" data-testid="commerce-clients">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <form className="relative flex-1" onSubmit={e => { e.preventDefault(); setQuery(q); }}>
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Search name, email or company" value={q} onChange={e => setQ(e.target.value)} data-testid="clients-search" />
        </form>
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger className="sm:w-48" data-testid="clients-stage-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            {STAGE_ORDER.map(s => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setAdding(true)} data-testid="add-client-btn"><Plus className="h-4 w-4 mr-1" />Add client</Button>
      </div>
      {loading ? <Loading /> : rows.length ? (
        <Panel>
          <DataTable testid="clients-table" rowKey="client_id" rows={rows} onRowClick={setSelected} columns={[
            { key: "name", label: "Client", render: r => <div data-testid={`client-row-${r.client_id}`}><p className="font-medium">{r.name}</p><p className="text-xs text-slate-500">{r.email}{r.company ? ` · ${r.company}` : ""}</p></div> },
            { key: "stage", label: "Stage", render: r => <StageBadge stage={r.stage} /> },
            { key: "acquisition_source", label: "Source", render: r => <span className="capitalize">{r.acquisition_source}</span> },
            { key: "retainer", label: "Retainer", render: r => r.monthly_retainer?.status === "active" ? `£${r.monthly_retainer.amount}/mo` : "—" },
            { key: "updated_at", label: "Last activity", render: r => fmtDate(r.updated_at) },
          ]} />
        </Panel>
      ) : <EmptyState testid="clients-empty" icon={Users} title="No clients match" text="Enquiries, bookings and playbook leads create Commerce clients automatically." />}
      {selected && <ClientDetailSheet call={call} clientId={selected.client_id} onClose={() => { setSelected(null); reload(); }} />}
      {adding && <NewClientDialog call={call} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); reload(); }} />}
    </div>
  );
};
